import React from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";

// Mirror ForecastChart's palette: a blue actual/forecast line with a danger cap.
const SPEND_COLOR = "hsl(var(--chart-1))";
const CAP_COLOR = "hsl(var(--danger))";
// A neutral reference colour for the Plan line (the month's planned cumulative
// spend trajectory) — the actual line above it = running ahead of plan.
const PACE_COLOR = "hsl(var(--chart-pred))";
// Chart structural tokens.
const GRID_COLOR = "hsl(var(--chart-grid))";
const AXIS_COLOR = "hsl(var(--chart-axis))";

// eurFull formats a whole-euro value, e.g. "€24,000" — the precise figure used in
// the tooltip and on the Y axis (the compact "€24k" form lives on the KPI tiles).
function eurFull(v) {
  const n = Math.round(Number(v) || 0);
  return `€${n.toLocaleString("en-US")}`;
}

// fmtDate normalises a date to dayjs and formats it. Tolerate a legacy YYYYMMDD id
// so a lagging backend can't break the axis.
function fmtDate(v, pattern) {
  if (!v) return "";
  const s = /^\d{8}$/.test(v) ? `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}` : v;
  const d = dayjs(s);
  return d.isValid() ? d.format(pattern) : String(v);
}

// LegendSwatch renders the small marker used in the chart legend as inline SVG so
// it always paints crisply (mirrors ForecastChart).
function LegendSwatch({ kind, color }) {
  return (
    <svg width="18" height="10" aria-hidden="true" className="shrink-0">
      <line
        x1="0"
        y1="5"
        x2="18"
        y2="5"
        stroke={color}
        strokeWidth="2.5"
        strokeDasharray={kind === "dashed" ? "5 3" : undefined}
        strokeLinecap="round"
      />
    </svg>
  );
}
LegendSwatch.propTypes = { kind: PropTypes.string, color: PropTypes.string };

// ChartLegend mirrors the forecast legend: small markers + muted labels.
function ChartLegend({ hasCap, hasPlan }) {
  const items = [
    { key: "actual", label: "Actual", kind: "solid", color: SPEND_COLOR },
    { key: "forecast", label: "Forecast", kind: "dashed", color: SPEND_COLOR },
  ];
  if (hasPlan) items.push({ key: "plan", label: "Plan", kind: "dashed", color: PACE_COLOR });
  if (hasCap) items.push({ key: "cap", label: "Cap", kind: "dashed", color: CAP_COLOR });
  return (
    <ul className="mt-2 flex list-none flex-wrap items-center justify-center gap-x-5 gap-y-1 p-0">
      {items.map((it) => (
        <li key={it.key} className="flex items-center gap-1.5">
          <LegendSwatch kind={it.kind} color={it.color} />
          <span className="text-xs text-text-2">{it.label}</span>
        </li>
      ))}
    </ul>
  );
}
ChartLegend.propTypes = { hasCap: PropTypes.bool, hasPlan: PropTypes.bool };

// BurndownTooltip mirrors ForecastChart's tooltip: a "dddd, MMMM D" header, then
// the day's cumulative spend in a large figure, plus the cap for context.
function BurndownTooltip({ active, payload, cap }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload || {};
  // actual is the realised cumulative spend up to today; once it runs out we fall
  // back to the forecast continuation.
  const isActual = p.actual != null;
  const label = isActual ? "Spent" : "Projected";
  const val = isActual ? p.actual : p.forecast;
  if (val == null) return null;
  return (
    <div className="rounded-card border border-card-bd bg-card px-3.5 py-3">
      <div className="mb-1.5 text-sm font-semibold text-text-2">
        {fmtDate(p.date, "dddd, MMMM D")}
      </div>
      <div className="mb-1">
        <div className="text-xs text-text-2">{label}</div>
        <div className="text-xl font-semibold tabular-nums text-text">{eurFull(val)}</div>
      </div>
      {p.plan != null && (
        <div className="text-xs text-text-2">
          Plan <span className="tabular-nums text-text">{eurFull(p.plan)}</span>
        </div>
      )}
      {cap != null && cap > 0 && (
        <div className="text-xs text-text-2">
          Cap <span className="tabular-nums text-text">{eurFull(cap)}</span>
        </div>
      )}
    </div>
  );
}
BurndownTooltip.propTypes = { active: PropTypes.bool, payload: PropTypes.array, cap: PropTypes.number };

// BudgetBurndownChart shows one budget's cumulative actual (solid) + forecast
// (dashed) spend against a dashed cap line — the recharts twin of ForecastChart.
// `cap` may be null/0 (the "Total" view) → no cap line is drawn. `actual_cum` is
// null on days after today, splitting the actual line from the forecast.
const BudgetBurndownChart = ({ cap, series, projectionLow, projectionHigh }) => {
  if (!Array.isArray(series) || series.length === 0) {
    return <div className="h-[120px] rounded-card bg-surface-2" data-testid="burndown-empty" />;
  }

  const hasCap = cap != null && Number(cap) > 0;

  // The Plan line is the backend-supplied `plan_cum` curve (the month's planned
  // cumulative spend trajectory). The actual line sitting above it = running ahead
  // of plan — exactly what the "spend vs plan" verdict reports. We draw it dotted in
  // a neutral colour so it reads as a reference rather than a forecast.
  const hasPlan = series.some((p) => Number.isFinite(Number(p.plan_cum)));

  // The projection range band shades the uncertainty around the forecast tail. It
  // spans only the forecast region (from "today", the last actual point, to month
  // end); at "today" both bounds equal the actual value there (zero width), widening
  // linearly to projectionLow/High at the last day.
  const lowN = Number(projectionLow);
  const highN = Number(projectionHigh);
  const hasBand =
    projectionLow != null && projectionHigh != null && Number.isFinite(lowN) && Number.isFinite(highN);

  const data = [];
  let lastActualIdx = -1;
  series.forEach((p, i) => {
    if (p.actual_cum != null) lastActualIdx = i;
  });
  // The band anchors at the last actual value ("today") and the last datum index.
  const anchorActual =
    lastActualIdx >= 0 ? Number(series[lastActualIdx].actual_cum) : null;
  const lastIdx = series.length - 1;
  series.forEach((p, i) => {
    const actual = p.actual_cum != null ? Number(p.actual_cum) : null;
    // Draw the forecast line only from the last actual point forward (or the whole
    // series if there's no actual yet), so the dashed forecast and solid actual
    // overlap by exactly one point at "today".
    const showForecast = lastActualIdx === -1 || i >= lastActualIdx;
    const planVal = Number(p.plan_cum);

    // Interpolate the band across the forecast region. `t` runs 0 at "today" to 1 at
    // the last day; null outside the forecast tail so the shaded band only appears
    // there.
    let rangeBase = null;
    let rangeSpan = null;
    if (hasBand && anchorActual != null && lastActualIdx >= 0 && i >= lastActualIdx) {
      const denom = lastIdx - lastActualIdx;
      const t = denom > 0 ? (i - lastActualIdx) / denom : 1;
      const low = anchorActual + (lowN - anchorActual) * t;
      const high = anchorActual + (highN - anchorActual) * t;
      rangeBase = low;
      rangeSpan = high - low;
    }

    data.push({
      date: p.day,
      actual,
      forecast: showForecast ? Number(p.forecast_cum) : null,
      plan: Number.isFinite(planVal) ? planVal : null,
      rangeBase,
      rangeSpan,
    });
  });

  return (
    <div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 12, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: AXIS_COLOR }}
              tickLine={false}
              axisLine={{ stroke: GRID_COLOR }}
              minTickGap={28}
              tickMargin={8}
              tickFormatter={(v) => fmtDate(v, "MMM D")}
            />
            <YAxis
              tick={{ fontSize: 12, fill: AXIS_COLOR }}
              tickLine={false}
              axisLine={false}
              width={64}
              domain={[0, "auto"]}
              tickFormatter={(v) => eurFull(v)}
            />
            <Tooltip
              content={<BurndownTooltip cap={hasCap ? Number(cap) : null} />}
              cursor={{ stroke: AXIS_COLOR, strokeWidth: 1, strokeDasharray: "3 3" }}
            />
            {hasCap && (
              <ReferenceLine
                y={Number(cap)}
                stroke={CAP_COLOR}
                strokeDasharray="4 4"
                label={{ value: "Cap", position: "insideTopRight", fontSize: 10, fill: CAP_COLOR, dy: -2 }}
              />
            )}
            {/* Projection range band, drawn first so the lines sit on top. A
                transparent base area lifts a faint shaded span area to sit between
                rangeLow (rangeBase) and rangeHigh (rangeBase + rangeSpan). */}
            {hasBand && (
              <Area type="monotone" dataKey="rangeBase" stackId="band" stroke="none" fill="transparent" isAnimationActive={false} connectNulls />
            )}
            {hasBand && (
              <Area type="monotone" dataKey="rangeSpan" stackId="band" stroke="none" fill={GRID_COLOR} fillOpacity={0.5} isAnimationActive={false} connectNulls />
            )}
            {/* Plan reference (backend plan_cum), dotted, drawn before the
                actual/forecast lines so they sit on top of it. */}
            {hasPlan && (
              <Line type="monotone" dataKey="plan" stroke={PACE_COLOR} strokeWidth={1.5} strokeDasharray="2 3" dot={false} isAnimationActive={false} connectNulls />
            )}
            {/* No connectNulls: the solid actual must stop at today, where the
                dashed forecast takes over. */}
            <Line type="monotone" dataKey="actual" stroke={SPEND_COLOR} strokeWidth={2.5} dot={false} isAnimationActive={false} connectNulls={false} />
            <Line type="monotone" dataKey="forecast" stroke={SPEND_COLOR} strokeWidth={2.5} strokeDasharray="5 3" dot={false} isAnimationActive={false} connectNulls={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <ChartLegend hasCap={hasCap} hasPlan={hasPlan} />
    </div>
  );
};

BudgetBurndownChart.propTypes = {
  cap: PropTypes.number,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.string,
      actual_cum: PropTypes.number, // may be null
      forecast_cum: PropTypes.number,
      plan_cum: PropTypes.number, // may be null
    })
  ),
  projectionLow: PropTypes.number,
  projectionHigh: PropTypes.number,
};

BudgetBurndownChart.defaultProps = {
  cap: 0,
  series: [],
  projectionLow: null,
  projectionHigh: null,
};

export default BudgetBurndownChart;
